import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Download, RefreshCw, UploadCloud, ChevronDown, ChevronUp, Tag } from "lucide-react";
import { toast } from "sonner";
import { ORDER_STEPS } from "@/data/services";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const STATUS_COLORS = {
  awaiting_payment: "bg-slate-500/15 text-slate-400",
  received: "bg-blue-500/15 text-blue-400",
  questionnaire_received: "bg-amber-500/15 text-amber-400",
  hr_review: "bg-violet-500/15 text-violet-400",
  draft_ready: "bg-amber-500/15 text-amber-300",
  completed: "bg-emerald-500/15 text-emerald-400",
};

const CATEGORIES = ["Graduate Visa Series", "UK ATS Guide", "Skilled Worker Visa CVs", "Interview Strategy", "UK Market Trends"];
const EMPTY_POST = { title: "", category: CATEGORIES[0], excerpt: "", content: "", cover_image: "", published: true };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [partnerships, setPartnerships] = useState([]);
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", percent: 10, label: "Referral discount" });
  const [delivering, setDelivering] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // null | {mode:'create'} | {mode:'edit', post}
  const [form, setForm] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) return navigate("/auth?next=/admin");
    if (!user) return;
    if (user.role !== "admin") return navigate("/dashboard");
    refresh();
  }, [user, navigate]);

  const refresh = () => {
    setLoading(true);
    Promise.all([api.get("/admin/orders"), api.get("/admin/enquiries"), api.get("/admin/blogs"), api.get("/admin/bookings"), api.get("/admin/partnerships"), api.get("/admin/promos")])
      .then(([o, e, b, bk, p, pr]) => {
        setOrders(o.data);
        setEnquiries(e.data);
        setBlogs(b.data);
        setBookings(bk.data);
        setPartnerships(p.data);
        setPromos(pr.data);
      })
      .catch(() => toast.error("Failed to load admin data"))
      .finally(() => setLoading(false));
  };

  const setStatus = async (orderId, status) => {
    try {
      await api.patch(`/admin/orders/${orderId}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status } : o)));
      toast.success("Order status updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePost = async () => {
    setSaving(true);
    try {
      if (editor.mode === "create") {
        await api.post("/admin/blogs", form);
        toast.success("Article published");
      } else {
        await api.put(`/admin/blogs/${editor.post.id}`, form);
        toast.success("Article updated");
      }
      setEditor(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    try {
      await api.delete(`/admin/blogs/${id}`);
      setBlogs((prev) => prev.filter((b) => b.id !== id));
      toast.success("Article deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user === null || (loading && orders.length === 0)) {
    return <div className="mx-auto max-w-7xl px-5 py-32 text-sm text-slate-500" data-testid="admin-loading">Loading admin console…</div>;
  }

  const deliverFile = async (orderId, file) => {
    if (!file) return;
    setDelivering((d) => ({ ...d, [orderId]: true }));
    try {
      const data = new FormData();
      data.append("file", file);
      await api.post(`/admin/orders/${orderId}/deliver`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: "draft_ready" } : o)));
      toast.success("Documents delivered — client notified by email");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDelivering((d) => ({ ...d, [orderId]: false }));
    }
  };

  const createPromo = async () => {
    try {
      await api.post("/admin/promos", promoForm);
      toast.success(`Code ${promoForm.code.toUpperCase()} is live`);
      setPromoForm({ code: "", percent: 10, label: "Referral discount" });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePromo = async (code) => {
    try {
      const { data } = await api.patch(`/admin/promos/${code}`);
      setPromos((prev) => prev.map((p) => (p.code === code ? { ...p, active: data.active } : p)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const tabs = [
    { id: "orders", label: `Orders (${orders.length})` },
    { id: "bookings", label: `Bookings (${bookings.length})` },
    { id: "enquiries", label: `Enquiries (${enquiries.length})` },
    { id: "partnerships", label: `Partnerships (${partnerships.length})` },
    { id: "promos", label: `Referral Codes (${promos.length})` },
    { id: "blog", label: `Blog (${blogs.length})` },
  ];

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-16 lg:px-8 lg:pt-20" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="overline-label">Admin Console</span>
          <h1 className="mt-3 font-serif text-4xl font-semibold text-slate-50" data-testid="admin-title">RMS Group Operations</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={refresh} className="btn-ghost !px-5 !py-2.5" data-testid="admin-refresh-btn"><RefreshCw size={15} /> Refresh</button>
          <button onClick={async () => { await logout(); navigate("/"); }} className="btn-ghost !px-5 !py-2.5" data-testid="admin-logout-btn"><LogOut size={15} /> Sign Out</button>
        </div>
      </div>

      <div className="mt-10 flex gap-2 border-b border-slate-800" data-testid="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`-mb-px border-b-2 px-5 py-3 text-sm font-semibold transition-colors duration-200 ${tab === t.id ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-slate-500 hover:text-slate-300"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ORDERS TAB */}
      {tab === "orders" && (
        <div className="card-surface mt-8 overflow-x-auto" data-testid="admin-orders-table">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-700/60 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                <th className="px-6 py-4">Client</th>
                <th className="px-6 py-4">Package</th>
                <th className="px-6 py-4">Amount</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Intake</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Deliver</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-500" data-testid="admin-orders-empty">No orders yet.</td></tr>
              )}
              {orders.map((o) => (
                <Fragment key={o.id}>
                  <tr className="border-b border-slate-800/60" data-testid={`admin-order-row-${o.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-100">{o.user_name}</div>
                      <div className="text-xs text-slate-500">{o.user_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-slate-300">{o.package_name}</div>
                      {o.delivery_days && (
                        <div className={`mt-1 font-mono text-[10px] uppercase tracking-wider ${o.delivery_speed === "advanced" ? "text-[#C5A059]" : "text-slate-500"}`} data-testid={`admin-delivery-${o.id}`}>
                          {o.delivery_speed === "advanced" ? "Advanced · " : "Basic · "}{o.delivery_days}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 font-mono text-slate-200">£{Number(o.amount).toFixed(2)}</td>
                    <td className="px-6 py-4 font-mono text-xs text-slate-400">{new Date(o.created_at).toLocaleDateString("en-GB")}</td>
                    <td className="px-6 py-4">
                      {o.intake ? (
                        <div className="text-xs text-slate-400" data-testid={`admin-intake-info-${o.id}`}>
                          <div>{o.intake.target_industry} · {o.intake.visa_status}</div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-3">
                            {o.intake.cv_file && (
                              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/intake-file/${o.id}`} className="inline-flex items-center gap-1 text-[#C5A059] hover:underline" data-testid={`admin-download-cv-${o.id}`}>
                                <Download size={12} /> {o.intake.cv_file}
                              </a>
                            )}
                            <button
                              onClick={() => setExpanded((x) => ({ ...x, [o.id]: !x[o.id] }))}
                              className="inline-flex items-center gap-1 text-slate-400 transition-colors hover:text-[#C5A059]"
                              data-testid={`admin-intake-expand-${o.id}`}
                            >
                              {expanded[o.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Brief
                            </button>
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-600">Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={o.status}
                        onChange={(e) => setStatus(o.id, e.target.value)}
                        data-testid="admin-order-status-select"
                        className={`rounded-lg border border-slate-700 bg-[#0F172A] px-3 py-2 font-mono text-xs outline-none ${STATUS_COLORS[o.status] || ""}`}
                      >
                        {ORDER_STEPS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      {o.status !== "awaiting_payment" ? (
                        <label
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-[#C5A059] hover:text-[#C5A059] ${delivering[o.id] ? "opacity-50" : ""}`}
                          data-testid={`admin-deliver-btn-${o.id}`}
                        >
                          <UploadCloud size={13} />
                          {delivering[o.id] ? "Uploading…" : "Deliver files"}
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            className="hidden"
                            disabled={delivering[o.id]}
                            onChange={(e) => { deliverFile(o.id, e.target.files[0]); e.target.value = ""; }}
                            data-testid={`admin-deliver-input-${o.id}`}
                          />
                        </label>
                      ) : (
                        <span className="text-xs text-slate-600">—</span>
                      )}
                    </td>
                  </tr>
                  {expanded[o.id] && o.intake && (
                    <tr key={`${o.id}-brief`} className="border-b border-slate-800/60 bg-[#0C1222]" data-testid={`admin-intake-brief-${o.id}`}>
                      <td colSpan={7} className="px-6 py-5">
                        <div className="grid gap-4 text-xs leading-relaxed sm:grid-cols-2 lg:grid-cols-4">
                          <div><span className="font-mono uppercase tracking-wider text-slate-500">Target roles</span><p className="mt-1 text-slate-300">{o.intake.target_roles}</p></div>
                          <div><span className="font-mono uppercase tracking-wider text-slate-500">Visa status</span><p className="mt-1 text-slate-300">{o.intake.visa_status}</p></div>
                          <div className="sm:col-span-2"><span className="font-mono uppercase tracking-wider text-slate-500">Achievements</span><p className="mt-1 whitespace-pre-line text-slate-300">{o.intake.achievements}</p></div>
                          {o.intake.notes && <div className="sm:col-span-2 lg:col-span-4"><span className="font-mono uppercase tracking-wider text-slate-500">Notes</span><p className="mt-1 text-slate-300">{o.intake.notes}</p></div>}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* BOOKINGS TAB */}
      {tab === "bookings" && (
        <div className="card-surface mt-8 overflow-x-auto" data-testid="admin-bookings-table">
          <table className="w-full min-w-[700px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-700/60 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                <th className="px-6 py-4">Client</th>
                <th className="px-6 py-4">Package</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4">Time (GMT)</th>
              </tr>
            </thead>
            <tbody>
              {bookings.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-10 text-center text-slate-500" data-testid="admin-bookings-empty">No interview sessions booked yet.</td></tr>
              )}
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-slate-800/60 last:border-0" data-testid={`admin-booking-row-${b.id}`}>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-slate-100">{b.user_name}</div>
                    <div className="text-xs text-slate-500">{b.user_email}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-300">{b.package_name}</td>
                  <td className="px-6 py-4 font-mono text-xs text-slate-200">{new Date(b.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long" })}</td>
                  <td className="px-6 py-4 font-mono text-xs text-[#C5A059]">{b.time} – {(parseInt(b.time.split(":")[0]) + 1).toString().padStart(2, "0")}:{b.time.split(":")[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* PARTNERSHIPS TAB */}
      {tab === "partnerships" && (
        <div className="mt-8 space-y-4" data-testid="admin-partnerships-list">
          {partnerships.length === 0 && <p className="card-surface p-10 text-center text-sm text-slate-500" data-testid="admin-partnerships-empty">No workshop requests yet.</p>}
          {partnerships.map((p) => (
            <div key={p.id} className="card-surface p-6" data-testid={`admin-partnership-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="font-semibold text-slate-100">{p.org_name}</span>
                  <span className="ml-3 text-sm text-slate-500">{p.contact_name} · {p.email}{p.phone ? ` · ${p.phone}` : ""}</span>
                </div>
                <span className="font-mono text-xs text-slate-500">{new Date(p.created_at).toLocaleString("en-GB")}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-wider">
                <span className="rounded-full bg-[#C5A059]/15 px-3 py-1 text-[#C5A059]">{p.org_type}</span>
                <span className="rounded-full bg-slate-700/40 px-3 py-1 text-slate-300">{p.workshop_type}</span>
                <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-emerald-400">{p.students_count} students</span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* REFERRAL CODES TAB */}
      {tab === "promos" && (
        <div className="mt-8" data-testid="admin-promos-panel">
          <div className="card-surface p-8">
            <h2 className="flex items-center gap-3 font-serif text-2xl font-semibold text-slate-50">
              <Tag size={22} className="text-[#C5A059]" /> Create a referral code
            </h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-4">
              <input value={promoForm.code} onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} className="input-dark font-mono uppercase" placeholder="CODE" data-testid="promo-create-code-input" />
              <input type="number" min={1} max={90} value={promoForm.percent} onChange={(e) => setPromoForm({ ...promoForm, percent: Number(e.target.value) })} className="input-dark" placeholder="% off" data-testid="promo-create-percent-input" />
              <input value={promoForm.label} onChange={(e) => setPromoForm({ ...promoForm, label: e.target.value })} className="input-dark sm:col-span-1" placeholder="Label" data-testid="promo-create-label-input" />
              <button onClick={createPromo} disabled={promoForm.code.length < 3} className="btn-gold !py-3 disabled:opacity-50" data-testid="promo-create-btn">
                <Plus size={15} /> Create
              </button>
            </div>
          </div>
          <div className="card-surface mt-6 overflow-x-auto">
            <table className="w-full min-w-[600px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-700/60 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                  <th className="px-6 py-4">Code</th>
                  <th className="px-6 py-4">Discount</th>
                  <th className="px-6 py-4">Uses</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {promos.map((p) => (
                  <tr key={p.code} className="border-b border-slate-800/60 last:border-0" data-testid={`admin-promo-row-${p.code}`}>
                    <td className="px-6 py-4">
                      <div className="font-mono font-semibold text-slate-100">{p.code}</div>
                      <div className="text-xs text-slate-500">{p.label}</div>
                    </td>
                    <td className="px-6 py-4 font-mono text-[#C5A059]">{p.percent}%</td>
                    <td className="px-6 py-4 font-mono text-slate-300">{p.uses || 0}</td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider ${p.active ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
                        {p.active ? "Live" : "Paused"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button onClick={() => togglePromo(p.code)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-[#C5A059] hover:text-[#C5A059]" data-testid={`promo-toggle-${p.code}`}>
                        {p.active ? "Pause" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ENQUIRIES TAB */}
      {tab === "enquiries" && (
        <div className="mt-8 space-y-4" data-testid="admin-enquiries-list">
          {enquiries.length === 0 && <p className="card-surface p-10 text-center text-sm text-slate-500" data-testid="admin-enquiries-empty">No enquiries yet.</p>}
          {enquiries.map((e) => (
            <div key={e.id} className="card-surface p-6" data-testid={`admin-enquiry-${e.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <span className="font-semibold text-slate-100">{e.name}</span>
                  <span className="ml-3 text-sm text-slate-500">{e.email}{e.phone ? ` · ${e.phone}` : ""}</span>
                </div>
                <span className="font-mono text-xs text-slate-500">{new Date(e.created_at).toLocaleString("en-GB")}</span>
              </div>
              {e.target && <div className="mt-2 font-mono text-xs text-[#C5A059]">Target: {e.target}</div>}
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{e.message}</p>
            </div>
          ))}
        </div>
      )}

      {/* BLOG TAB */}
      {tab === "blog" && (
        <div className="mt-8" data-testid="admin-blog-panel">
          {editor ? (
            <div className="card-surface p-8" data-testid="admin-blog-editor">
              <h2 className="font-serif text-2xl font-semibold text-slate-50">{editor.mode === "create" ? "New Article" : `Edit: ${editor.post.title}`}</h2>
              <div className="mt-8 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Title *</label>
                    <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-dark" data-testid="admin-blog-title-input" placeholder="Article title" />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Category</label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark" data-testid="admin-blog-category-select">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Excerpt *</label>
                  <input value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="input-dark" data-testid="admin-blog-excerpt-input" placeholder="One-sentence hook shown on the listing" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Cover Image URL</label>
                  <input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="input-dark" data-testid="admin-blog-cover-input" placeholder="https://… (optional)" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">Content *</label>
                  <textarea rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="input-dark resize-y" data-testid="admin-blog-content-input" placeholder="Write the article. Blank lines create paragraphs." />
                </div>
                <label className="flex items-center gap-3 text-sm text-slate-300">
                  <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-4 w-4 accent-[#C5A059]" data-testid="admin-blog-published-toggle" />
                  Published (visible on The HR Insight)
                </label>
                <div className="flex gap-3 pt-2">
                  <button onClick={savePost} disabled={saving || !form.title || !form.excerpt || !form.content} className="btn-gold disabled:opacity-50" data-testid="admin-blog-save-btn">
                    {saving ? "Saving…" : editor.mode === "create" ? "Publish Article" : "Save Changes"}
                  </button>
                  <button onClick={() => setEditor(null)} className="btn-ghost" data-testid="admin-blog-cancel-btn">Cancel</button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <button onClick={() => { setForm(EMPTY_POST); setEditor({ mode: "create" }); }} className="btn-gold" data-testid="blog-admin-create-post-btn">
                <Plus size={16} /> New Article
              </button>
              <div className="card-surface mt-6 overflow-x-auto">
                <table className="w-full min-w-[700px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-700/60 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">
                      <th className="px-6 py-4">Title</th>
                      <th className="px-6 py-4">Category</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Date</th>
                      <th className="px-6 py-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blogs.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-slate-500">No articles yet.</td></tr>}
                    {blogs.map((b) => (
                      <tr key={b.id} className="border-b border-slate-800/60 last:border-0" data-testid={`admin-blog-row-${b.id}`}>
                        <td className="px-6 py-4 font-medium text-slate-100">{b.title}</td>
                        <td className="px-6 py-4 text-slate-400">{b.category}</td>
                        <td className="px-6 py-4">
                          <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.1em] ${b.published ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>
                            {b.published ? "Live" : "Draft"}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-mono text-xs text-slate-500">{new Date(b.created_at).toLocaleDateString("en-GB")}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button onClick={() => { setForm({ title: b.title, category: b.category, excerpt: b.excerpt, content: b.content, cover_image: b.cover_image || "", published: b.published }); setEditor({ mode: "edit", post: b }); }} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 transition-colors hover:border-[#C5A059] hover:text-[#C5A059]" data-testid={`admin-blog-edit-${b.id}`}>
                              Edit
                            </button>
                            <button onClick={() => deletePost(b.id)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-red-400 transition-colors hover:border-red-500/50" data-testid={`admin-blog-delete-${b.id}`}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
